import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const SESSION_CHANNEL = 'betika_session';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Idle logout. Distinct from the JWT's own lifetime (JWT_EXPIRE, 7 days) —
  // this is the client deciding to drop a session that has gone quiet, and it
  // is what users actually experience as "logged out".
  //
  // Five minutes was short enough to interrupt ordinary work: filling in a
  // supplier application, reading a contract, or stepping away to find a
  // document all blew past it. Keep it well under JWT_EXPIRE, or the timer stops
  // being a clean logout and users start hitting 401s instead.
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour
  const WARNING_AT = 59 * 60 * 1000;         // warn a minute before

  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);

  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    sessionStorage.setItem('lastActivity', now);
    warningShownRef.current = false;
  }, []);

  // Cross-tab session sharing.
  //
  // sessionStorage is per-tab, so a new tab starts with no token and asks an
  // existing tab for one. This used to relay the JWT through localStorage,
  // which persists it to disk for as long as it takes the writing tab to clean
  // up — long enough for injected script to read it, and defeating the reason
  // the token is kept in sessionStorage at all. BroadcastChannel passes the
  // message in memory and never persists anything.
  const requestTokenFromOtherTab = () =>
    new Promise((resolve) => {
      if (typeof BroadcastChannel === 'undefined') return resolve(null);

      const channel = new BroadcastChannel(SESSION_CHANNEL);
      let done = false;

      const finish = (token) => {
        if (done) return;
        done = true;
        channel.close();
        resolve(token);
      };

      channel.onmessage = (event) => {
        if (event.data?.type === 'token_response' && event.data.token) {
          finish(event.data.token);
        }
      };

      channel.postMessage({ type: 'token_request' });
      setTimeout(() => finish(null), 300);
    });

  // Respond to other tabs and handle remote logout
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;

    const channel = new BroadcastChannel(SESSION_CHANNEL);

    channel.onmessage = (event) => {
      if (event.data?.type === 'token_request') {
        const token = sessionStorage.getItem('token');
        if (token) channel.postMessage({ type: 'token_response', token });
      }
      if (event.data?.type === 'logout') {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('lastActivity');
        setUser(null);
      }
    };

    return () => channel.close();
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!sessionStorage.getItem('token')) {
        const token = await requestTokenFromOtherTab();
        if (token) sessionStorage.setItem('token', token);
      }
      checkAuth();
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        const userData = response.data.user;
        // Only require email verification for suppliers
        // Internal users (procurement, legal, etc.) don't need email verification
        if (userData) {
          if (userData.role === 'supplier' && userData.isEmailVerified === false) {
            // Supplier email not verified, clear token to allow 2FA flow
            console.log('🟡 [AUTH] Supplier email not verified, clearing token for 2FA flow');
            sessionStorage.removeItem('token');
            setUser(null);
          } else {
            // Non-supplier or verified supplier - allow access
            setUser(userData);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        sessionStorage.removeItem('token');
        setUser(null);
      }
    } else {
      // Ensure user is null if no token
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const data = response.data;

      // Suppliers: the password was accepted and a sign-in code is already on
      // its way to their inbox. Staff continue on the authenticator branches.
      if (data.requiresEmailOtp) {
        return { success: true, requiresEmailOtp: true, email: data.email || email };
      }
      if (data.requiresTOTPSetup) {
        return { success: true, requiresTOTPSetup: true, email: data.email, qrCode: data.qrCode, secret: data.secret };
      }
      if (data.requiresTOTP) {
        return { success: true, requiresTOTP: true, email: data.email };
      }
      if (data.requiresEmailVerification || data.requiresVerification) {
        return { success: true, requiresVerification: true, email: data.email || email };
      }

      const { token, user } = data;
      if (!token || !user) throw new Error('Invalid response from server');
      sessionStorage.setItem('token', token);
      setUser(user);
      toast.success('Login successful!');
      return { success: true, user };
    } catch (error) {
      const data = error.response?.data;
      if (data?.requiresEmailOtp) return { success: true, requiresEmailOtp: true, email: data.email || email };
      if (data?.requiresTOTPSetup) return { success: true, requiresTOTPSetup: true, email: data.email, qrCode: data.qrCode, secret: data.secret };
      if (data?.requiresTOTP) return { success: true, requiresTOTP: true, email: data.email };
      if (data?.requiresEmailVerification || data?.requiresVerification) return { success: true, requiresVerification: true, email: data.email || email };
      const message = data?.message || error.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const setupTOTP = async (email, totpCode) => {
    try {
      const response = await api.post('/auth/setup-totp', { email, totpCode });
      const { token, user, backupCodes } = response.data;
      sessionStorage.setItem('token', token);
      setUser(user);
      return { success: true, user, backupCodes };
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid code';
      toast.error(message);
      return { success: false, message };
    }
  };

  const verifyTOTP = async (email, totpCode) => {
    try {
      const response = await api.post('/auth/verify-totp', { email, totpCode });
      const { token, user } = response.data;
      sessionStorage.setItem('token', token);
      setUser(user);
      toast.success('Login successful!');
      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid code';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Redeem the code emailed at registration. Proving ownership of the address
  // does not sign anyone in — no token is returned — but it is now the only
  // enrolment step a supplier has, so the next stop is the sign-in screen.
  const verifyEmail = async (email, otpCode) => {
    try {
      const { data } = await api.post('/auth/verify-email', { email, otpCode });
      return {
        success: true,
        emailVerified: true,
        email: data.email || email,
        message: data.message
      };
    } catch (error) {
      const message = error.response?.data?.message || 'That code is not valid or has expired.';
      toast.error(message);
      return { success: false, message };
    }
  };

  const resendVerification = async (email) => {
    try {
      const { data } = await api.post('/auth/resend-verification', { email });
      toast.success(data.message || 'If that address needs verifying, we have sent a new code.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Could not send a new code.';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Suppliers only. The password is sent again because the server re-checks it
  // before mailing a code.
  const requestLoginOtp = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login-otp/request', { email, password });
      return { success: true, message: data.message };
    } catch (error) {
      const data = error.response?.data;
      if (data?.requiresEmailVerification) {
        return { success: false, requiresVerification: true, email: data.email, message: data.message };
      }
      const message = data?.message || 'Could not send a sign-in code.';
      toast.error(message);
      return { success: false, message };
    }
  };

  const verifyLoginOtp = async (email, otpCode) => {
    try {
      const { data } = await api.post('/auth/login-otp/verify', { email, otpCode });
      sessionStorage.setItem('token', data.token);
      setUser(data.user);
      toast.success('Login successful!');
      return { success: true, user: data.user };
    } catch (error) {
      const message = error.response?.data?.message || 'That code is not valid or has expired.';
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      console.log('🔵 [REGISTER] Starting registration for:', userData.email);
      const response = await api.post('/auth/register', userData);
      console.log('🔵 [REGISTER] Response status:', response.status);
      console.log('🔵 [REGISTER] Response data:', JSON.stringify(response.data, null, 2));
      
      // Check if email verification is required
      if (response.data && (response.data.requiresEmailVerification || response.data.requiresVerification)) {
        console.log('🟡 [REGISTER] Email verification required - NOT setting user or token');
        console.log('🟡 [REGISTER] Returning requiresVerification flag');
        // IMPORTANT: Do NOT set user or token - user must verify email first
        return { 
          success: true, 
          requiresVerification: true,
          email: response.data.email || userData.email,
          message: response.data.message || 'Please verify your email'
        };
      }
      
      // Only set token and user if verification is NOT required
      const { token, user } = response.data || {};
      
      if (!token || !user) {
        console.error('🔴 [REGISTER] Missing token or user in response');
        throw new Error('Invalid response from server');
      }
      
      console.log('🟢 [REGISTER] Setting token and user');
      sessionStorage.setItem('token', token);
      setUser(user);
      toast.success('Registration successful!');
      
      return { success: true, user };
    } catch (error) {
      console.error('🔴 [REGISTER] Error caught:', error);
      console.error('🔴 [REGISTER] Error response:', error.response?.data);
      console.error('🔴 [REGISTER] Error status:', error.response?.status);
      
      // Check if it's a verification requirement in error response
      if (error.response?.data?.requiresEmailVerification || error.response?.data?.requiresVerification) {
        console.log('🟡 [REGISTER] Email verification required (from error response)');
        return { 
          success: true, 
          requiresVerification: true,
          email: error.response.data.email || userData.email,
          message: error.response.data.message || 'Please verify your email'
        };
      }
      
      const message = error.response?.data?.message || error.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };



  useEffect(() => {
    if (!user) return;

    // Check if the user was already idle before the page loaded (e.g. machine woke from sleep)
    const stored = parseInt(sessionStorage.getItem('lastActivity') || '0', 10);
    const idleBeforeLoad = stored ? Date.now() - stored : 0;
    if (idleBeforeLoad >= INACTIVITY_TIMEOUT) {
      logout('Your session has expired due to inactivity.');
      return;
    }

    lastActivityRef.current = stored || Date.now();
    sessionStorage.setItem('lastActivity', lastActivityRef.current);
    warningShownRef.current = false;

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= INACTIVITY_TIMEOUT) {
        logout('Your session has expired due to inactivity.');
      } else if (idle >= WARNING_AT && !warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning('You will be logged out in 1 minute due to inactivity.');
      }
    }, 30_000);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const logout = (message = 'Logged out successfully') => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('lastActivity');
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.postMessage({ type: 'logout' });
      channel.close();
    }
    setUser(null);
    toast.info(message);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setUser(null);
      return null;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      setUser(userData || null);
      return userData || null;
    } catch (error) {
      console.error('Refresh user failed:', error);
      sessionStorage.removeItem('token');
      setUser(null);
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    setupTOTP,
    verifyTOTP,
    verifyEmail,
    resendVerification,
    requestLoginOtp,
    verifyLoginOtp,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

